import React, { useEffect, useState } from 'react';

import Modal from '../common/Modal';

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import { validateRequiredFields } from "../../utils/formValidation";
import { deepTrim } from "../../utils/formValidation";

const WabaSenderDetails = () => {

  const waApi = useWaApi();
  // Only a SYSTEM_ADMIN or ADMIN may change anything here; a USER gets a read-only view. The service refuses
  // the writes too, so this just keeps the controls out of sight.
  const { canManageWhatsAppSetup } = useUserProfileAccess();


  const [senders, setSenders] = useState([]);
  const [wabas, setWabas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newSender, setNewSender] = useState({
    cc: '',
    senderPhoneNumber: '',
    verifiedName: '',
    description: '',
    wabaDetailId: '',
    status: false,
  });

  const [editDescription, setEditDescription] = useState('');
  const [editingSender, setEditingSender] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [setPinValues, setSetPinValues] = useState({});
  const [settingPinSenderId, setSettingPinSenderId] = useState(null);
  const [pinErrors, setPinErrors] = useState({});
  const [otpErrors, setOtpErrors] = useState({});
  const [registeringPinSenderId, setRegisteringPinSenderId] = useState(null);
  const [registerPinErrors, setRegisterPinErrors] = useState({});

  const [otpInputVisibleFor, setOtpInputVisibleFor] = useState(null);
  const [otpValues, setOtpValues] = useState({});
  const [verifyingOtpSenderId, setVerifyingOtpSenderId] = useState(null);
  const [fetchingOtpSenderId, setFetchingOtpSenderId] = useState(null);
  const [syncingSenderId, setSyncingSenderId] = useState(null);
  const [syncMessages, setSyncMessages] = useState({});
  const [syncErrors, setSyncErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const initialFilters = {
    id: null,
    senderPhoneNumber: null,
    wabaId: null,
    waAppName: null,
    status: null
  };
  const [filters, setFilters] = useState(initialFilters);

  const resetFilters = () => {
    clearMessages();
    setFilters(initialFilters);
  };

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(3);
  const [totalPages, setTotalPages] = useState(0);
  const [formError, setFormError] = useState({});
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);

  const clearMessages = () => {
    setCreateError(null);
    setCreateMessage(null);
    setPinErrors({});
    setOtpErrors({});
    setRegisterPinErrors({});
    setFormError({});
    setSyncMessages({});
    setSyncErrors({});
    setEditErrors({});
  };

  useEffect(() => {

    const fetchSenders = async () => {
      try {
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_SENDERS_BY_PAGE_ENDPOINT,
          {
            params: {
              ...filters,
              page,
              size,
            },
          }
        );

        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setSenders(object.content);
          setTotalPages(object.totalPages);
        } else {
          setError(`Error: ${message}`);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching WA sender details:', err);
        setError(err.message || 'An error occurred.');
        setLoading(false);
      }
    };

    const fetchWabas = async () => {
      try {
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WABA_DETAILS_ENDPOINT
        );
        const { message, object, httpStatus } = response.data;
        if (httpStatus === 200) {
          setWabas(object);
        } else {
          setError(`Error: ${message}`);
        }
      } catch (err) {
        console.error('Error fetching WABA details:', err);
        setError(err.message || 'Failed to load WABA details.');
      }
    };

    fetchSenders();
    fetchWabas();
  }, [waApi, page, size]);

  useEffect(() => {
    if (modalOpen) {
      setFormError({});
    }
  }, [modalOpen]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;

    setNewSender((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;

    setFilters((prev) => ({ ...prev, status: statusValue }));
  };

  const handleEditClick = (sender) => {
    clearMessages();
    setEditingSender(sender);
    setEditDescription(sender.description || "");
    setEditModalOpen(true);
  };

  const refreshCache = async () => {
    clearMessages();
    try {
      const response = await waApi.get(
        process.env.REACT_APP_REFRESH_WA_SENDER_CACHE_ENDPOINT
      );
      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while refreshing the cache.');
    }
  };

  const toggleActivationStatus = async (id, currentStatus) => {
    clearMessages();
    try {
      const newStatus = !currentStatus;
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WA_SENDER_STATUS_ENDPOINT}/${id}`,
        { status: newStatus }
      );
      setSenders((prev) =>
        prev.map((sender) => sender.id === id ? { ...sender, status: newStatus } : sender)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setCreateError(err.message || 'An error occurred while updating the status.');
    }
  };

  const handlePinInputChange = (senderId, value) => {
    if (/^\d{0,6}$/.test(value)) {
      setSetPinValues((prev) => ({ ...prev, [senderId]: value }));
      setPinErrors((prev) => ({ ...prev, [senderId]: null }));
    }
  };

  const handleSetPin = async (sender) => {
    clearMessages();
    const pin = setPinValues[sender.id] || '';
    if (!/^\d{6}$/.test(pin)) {
      setPinErrors((prev) => ({ ...prev, [sender.id]: 'PIN must be exactly 6 digits.' }));
      return;
    }
    setPinErrors((prev) => ({ ...prev, [sender.id]: null }));
    setSettingPinSenderId(sender.id);
    try {
      await waApi.post(
        process.env.REACT_APP_SET_WA_SENDER_PIN_ENDPOINT,
        {
          senderPhoneNumberId: sender.senderPhoneNumberId,
          pin
        }
      );
      setSenders((prev) =>
        prev.map((s) => s.id === sender.id ? { ...s, pinExists: true } : s)
      );
      setSetPinValues((prev) => {
        const updated = { ...prev };
        delete updated[sender.id];
        return updated;
      });
    } catch (err) {
      setPinErrors((prev) => ({ ...prev, [sender.id]: err.message || 'An error occurred while setting the PIN.' }));
    } finally {
      setSettingPinSenderId(null);
    }
  };

  const handleRegisterByPin = async (sender) => {
    clearMessages();
    setRegisterPinErrors((prev) => ({ ...prev, [sender.id]: null }));
    setRegisteringPinSenderId(sender.id);
    try {
      await waApi.post(
        `${process.env.REACT_APP_REGISTER_WA_SENDER_BY_PIN_ENDPOINT}/${sender.senderPhoneNumberId}`
      );
      setSenders((prev) =>
        prev.map((s) => s.id === sender.id ? { ...s, senderRegisteredByPin: true } : s)
      );
      setRegisterPinErrors((prev) => ({ ...prev, [sender.id]: null }));
    } catch (err) {
      setRegisterPinErrors((prev) => ({ ...prev, [sender.id]: err.message || 'An error occurred while registering the sender by PIN.' }));
    } finally {
      setRegisteringPinSenderId(null);
    }
  };

  const handleFetchOtp = async (sender) => {
    clearMessages();
    setOtpErrors((prev) => ({ ...prev, [sender.id]: null }));
    setFetchingOtpSenderId(sender.id);
    try {
      await waApi.post(
        process.env.REACT_APP_SET_WA_SENDER_VERIFY_METHOD_ENDPOINT,
        {
          senderPhoneNumberId: sender.senderPhoneNumberId,
          code_method: 'SMS'
        }
      );
      setOtpInputVisibleFor(sender.id);
      setOtpErrors((prev) => ({ ...prev, [sender.id]: null }));
    } catch (err) {
      setOtpErrors((prev) => ({ ...prev, [sender.id]: err.message || 'An error occurred while requesting the OTP.' }));
    } finally {
      setFetchingOtpSenderId(null);
    }
  };

  const handleOtpInputChange = (senderId, value) => {
    if (/^\d{0,6}$/.test(value)) {
      setOtpValues((prev) => ({ ...prev, [senderId]: value }));
      setOtpErrors((prev) => ({ ...prev, [senderId]: null }));
    }
  };

  const handleVerifyOtp = async (sender) => {
    clearMessages();
    const code = otpValues[sender.id] || '';
    if (!/^\d{6}$/.test(code)) {
      setOtpErrors((prev) => ({ ...prev, [sender.id]: 'OTP must be exactly 6 digits.' }));
      return;
    }
    setOtpErrors((prev) => ({ ...prev, [sender.id]: null }));
    setVerifyingOtpSenderId(sender.id);
    try {
      await waApi.post(
        process.env.REACT_APP_VERIFY_WA_SENDER_BY_CODE_ENDPOINT,
        {
          senderPhoneNumberId: sender.senderPhoneNumberId,
          code
        }
      );
      setSenders((prev) =>
        prev.map((s) => s.id === sender.id ? { ...s, senderNumberOTPVerified: true } : s)
      );
      setOtpValues((prev) => {
        const updated = { ...prev };
        delete updated[sender.id];
        return updated;
      });
      setOtpInputVisibleFor(null);
    } catch (err) {
      setOtpErrors((prev) => ({ ...prev, [sender.id]: err.message || 'An error occurred while verifying the OTP.' }));
    } finally {
      setVerifyingOtpSenderId(null);
    }
  };

  const handleSyncInfo = async (sender) => {
    clearMessages();
    setSyncingSenderId(sender.id);
    try {
      const response = await waApi.get(
        `${process.env.REACT_APP_SYNC_WA_SENDER_PHONE_NUMBER_ENDPOINT}/${sender.senderPhoneNumberId}`
      );
      const { message, object, httpStatus } = response.data;
      if (httpStatus === 200) {
        if (object) {
          setSenders((prev) =>
            prev.map((s) => s.id === sender.id ? { ...s, ...object } : s)
          );
        }
        setSyncMessages((prev) => ({ ...prev, [sender.id]: message }));
      } else {
        setSyncErrors((prev) => ({ ...prev, [sender.id]: message || 'Unable to sync Sender info.' }));
      }
    } catch (err) {
      setSyncErrors((prev) => ({ ...prev, [sender.id]: err.message || 'An error occurred while syncing the Sender info.' }));
    } finally {
      setSyncingSenderId(null);
    }
  };

  const handleEditSubmit = async (id) => {

    setCreateError(null);
    setCreateMessage(null);
    setEditErrors((prev) => ({ ...prev, [id]: null }));
    try {
      const response = await waApi.put(
        `${process.env.REACT_APP_PUT_WA_SENDER_ENDPOINT}/${id}`,
        {
          description: editDescription
        }
      );

      setSenders((prev) =>
        prev.map((sender) => sender.id === id ? { ...sender, description: editDescription } : sender)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setEditErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while updating the Sender Detail.' }));
    } finally {
      setEditingSender(null);
      setEditModalOpen(false);
    }
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    clearMessages();
    setFormError({});
    const validation = validateRequiredFields({
      cc: { value: newSender.cc, label: "Country Code" },
      senderPhoneNumber: { value: newSender.senderPhoneNumber, label: "Sender Phone Number" },
      wabaDetailId: { value: newSender.wabaDetailId, label: "WABA" },
    });
    if (Object.keys(validation.errors).length > 0) {
      setFormError(validation.errors);
      return;
    }
    try {
      const selectedWabaDetail = wabas.find((waba) => String(waba.id) === String(newSender.wabaDetailId));
      const payload = deepTrim({
        cc: newSender.cc,
        senderPhoneNumber: newSender.senderPhoneNumber,
        verifiedName: newSender.verifiedName,
        description: newSender.description,
        status: newSender.status,
        wabaDetail: selectedWabaDetail
      });
      const response = await waApi.post(
        `${process.env.REACT_APP_SET_WA_SENDER_ENDPOINT}`,
        payload
      );
      if (response.data.httpStatus === 200) {
        setSenders((prev) => [...prev, response.data.object]);
        setNewSender({ cc: '', senderPhoneNumber: '', verifiedName: '', description: '', wabaDetailId: '', status: true });
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while creating the sender detail.');
    } finally {
      setModalOpen(false);
    }
  };

  const applyFilters = async (pageParam) => {

    setLoading(true);
    setCreateError(null);
    setCreateMessage(null);
    var hitApplyFilter = null;
    if (page === 0) {
      hitApplyFilter = true;
    } else {
      hitApplyFilter = false;
    }
    if (pageParam === 0) {
      setPage(0);
    }
    if (hitApplyFilter) {
      try {
        const cleanedFilters = Object.fromEntries(
          Object.entries(filters).map(([key, value]) => [
            key,
            value === '' ? null : value,
          ])
        );
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_SENDERS_BY_PAGE_ENDPOINT,
          {
            params: {
              ...cleanedFilters,
              page,
              size,
            },
          }
        );

        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setSenders(object.content);
          setTotalPages(object.totalPages);
        } else {
          setCreateError(message);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching WA sender details:', err);
        setCreateError(err.message || 'An error occurred.');
        setLoading(false);
      }
    }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  const handleSizeChange = (e) => {
    setSize(e.target.value);
    setPage(0);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading WA sender details: {error}</div>;

  return (
    <div className="page-container">
      <h1>App Sender ID Details</h1>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>
      <h3>Filters</h3>
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleFilterInputChange}
            placeholder="Only numeric value"
          />
        </div>

        <div className="filter-item">
          <label>Sender Phone Number:</label>
          <input
            type="text"
            name="senderPhoneNumber"
            value={filters.senderPhoneNumber ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Business Account Id:</label>
          <input
            type="text"
            name="wabaId"
            value={filters.wabaId ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>App Name:</label>
          <input
            type="text"
            name="waAppName"
            value={filters.waAppName ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusChange} value={filters.status === null ? '' : filters.status ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <button type="button" onClick={() => applyFilters(0)}>Apply Filters</button>
        <button type="button" onClick={resetFilters}> Reset Filters</button>

      </div>

      {canManageWhatsAppSetup && (
        <div className="action-bar">
          <button
            onClick={() => {
              clearMessages();
              setNewSender({ cc: '', senderPhoneNumber: '', verifiedName: '', description: '', wabaDetailId: '', status: false });
              setModalOpen(true);
            }}
          >
            + Create New
          </button>

          <button type="button" onClick={refreshCache}>
            Refresh Cache
          </button>
        </div>
      )}

      {createMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { clearMessages(); setModalOpen(false); }}>
        <div className="page-container">
          {formError && Object.values(formError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(formError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>
                      {typeof err === "object" ? JSON.stringify(err) : String(err)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit} className="standard-form">
            <h3>Create New WA Sender Detail</h3>
            <div className="form-group">
              <label>Country Dial Code:</label>
              <input
                type="text"
                name="cc"
                value={newSender.cc}
                onChange={handleInputChange}
                placeholder="e.g. 91 or 1"
                required
              />
            </div>

            <div className="form-group">
              <label>Sender Phone Number:</label>
              <input
                type="text"
                name="senderPhoneNumber"
                value={newSender.senderPhoneNumber}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Verified Name:</label>
              <input
                type="text"
                name="verifiedName"
                value={newSender.verifiedName}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Description:</label>
              <input
                type="text"
                name="description"
                value={newSender.description}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>WABA:</label>
              <select
                name="wabaDetailId"
                value={newSender.wabaDetailId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a WABA</option>
                {wabas.map((waba) => (
                  <option key={waba.id} value={waba.id}>
                    {waba.wabaId}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Activation Status:</label>
              <input
                type="checkbox"
                name="status"
                checked={newSender.status}
                onChange={handleInputChange}
              />
            </div>
            <button type="submit">Create Sender</button>
          </form>
        </div>
      </Modal>

      {editModalOpen && editingSender && (
        <div className="modal-overlay">
          <div className="modal wa-popup">
            <div className="modal-header">
              <h3>WA Sender Detail</h3>
            </div>

            <div className="modal-body">
              <div className="standard-form">
                <div className="form-group">
                  <label>ID:</label>
                  <input type="text" value={editingSender.id ?? ''} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Sender Phone Number Id:</label>
                  <input type="text" value={editingSender.senderPhoneNumberId ?? ''} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Country Dial Code:</label>
                  <input type="text" value={editingSender.cc ?? ''} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Sender Phone Number:</label>
                  <input type="text" value={editingSender.senderPhoneNumber ?? ''} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Verified Name:</label>
                  <input type="text" value={editingSender.verifiedName ?? ''} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Description:</label>
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Business Account Id:</label>
                  <input type="text" value={editingSender.wabaDetail?.wabaId ?? ''} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>PIN Exists:</label>
                  <input type="text" value={editingSender.pinExists ? 'Yes' : 'No'} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Entered In WA:</label>
                  <input type="text" value={editingSender.senderNumberEnteredInWA ? 'Yes' : 'No'} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>OTP Verified:</label>
                  <input type="text" value={editingSender.senderNumberOTPVerified ? 'Yes' : 'No'} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Registered By PIN:</label>
                  <input type="text" value={editingSender.senderRegisteredByPin ? 'Yes' : 'No'} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Status:</label>
                  <input type="text" value={editingSender.status ? 'Active' : 'Inactive'} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Created At:</label>
                  <input type="text" value={new Date(editingSender.createdTimestamp).toLocaleString()} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Last Updated At:</label>
                  <input type="text" value={new Date(editingSender.updatedTimestamp).toLocaleString()} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
                <div className="form-group">
                  <label>Last Updated By:</label>
                  <input type="text" value={editingSender.lastUpdatedBy ?? ''} disabled style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-outline" onClick={() => { clearMessages(); setEditModalOpen(false); }}>Cancel</button>
              <button type="button" onClick={() => handleEditSubmit(editingSender.id)}>Save</button>
            </div>
          </div>
        </div>
      )}

      <h3>Existing WA Sender Details</h3>
      {senders.length === 0 ? (
        <p>No sender details available</p>
      ) : (
        <div className="table-responsive">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Sender Phone Number Id</th>
                <th>Country Dial Code</th>
                <th>Sender Phone Number</th>
                <th>Verified Name</th>
                <th>Description</th>
                <th>Business Account Id</th>
                <th>PIN Exists</th>
                <th>Entered In WA</th>
                <th>OTP Verified</th>
                <th>Registered By PIN</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Last Updated At</th>
                <th>Last Updated By</th>
                {canManageWhatsAppSetup && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {senders.map((sender) => (
                <tr key={sender.id}>
                  <td>{sender.id}</td>
                  <td>{sender.senderPhoneNumberId}</td>
                  <td>{sender.cc}</td>
                  <td>{sender.senderPhoneNumber}</td>
                  <td>{sender.verifiedName}</td>
                  <td>{sender.description}</td>
                  <td>{sender.wabaDetail?.wabaId}</td>
                  <td>
                    <div className="inline">
                      <span>{sender.pinExists ? 'Yes' : 'No'}</span>
                      {canManageWhatsAppSetup && !sender.pinExists && (
                        <>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="6-digit PIN"
                            value={setPinValues[sender.id] || ''}
                            onChange={(e) => handlePinInputChange(sender.id, e.target.value)}
                            style={{ width: '90px' }}
                          />
                          <button
                            onClick={() => handleSetPin(sender)}
                            disabled={settingPinSenderId === sender.id}
                          >
                            {settingPinSenderId === sender.id ? 'Setting...' : 'Set Pin'}
                          </button>
                        </>
                      )}
                    </div>
                    {pinErrors[sender.id] && (
                      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                        {pinErrors[sender.id]}
                      </div>
                    )}
                  </td>
                  <td>{sender.senderNumberEnteredInWA ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="inline">
                      <span>{sender.senderNumberOTPVerified ? 'Yes' : 'No'}</span>
                      {canManageWhatsAppSetup && !sender.senderNumberOTPVerified && (
                        otpInputVisibleFor === sender.id ? (
                          <>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              placeholder="6-digit OTP"
                              value={otpValues[sender.id] || ''}
                              onChange={(e) => handleOtpInputChange(sender.id, e.target.value)}
                              style={{ width: '100px' }}
                            />
                            <button
                              onClick={() => handleVerifyOtp(sender)}
                              disabled={verifyingOtpSenderId === sender.id}
                            >
                              {verifyingOtpSenderId === sender.id ? 'Submitting...' : 'Submit'}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleFetchOtp(sender)}
                            disabled={fetchingOtpSenderId === sender.id}
                          >
                            {fetchingOtpSenderId === sender.id ? 'Fetching...' : 'Fetch OTP'}
                          </button>
                        )
                      )}
                    </div>
                    {otpErrors[sender.id] && (
                      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                        {otpErrors[sender.id]}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="inline">
                      <span>{sender.senderRegisteredByPin ? 'Yes' : 'No'}</span>
                      {canManageWhatsAppSetup && !sender.senderRegisteredByPin && (
                        <button
                          onClick={() => handleRegisterByPin(sender)}
                          disabled={!sender.pinExists || registeringPinSenderId === sender.id}
                          title={!sender.pinExists ? 'Set a PIN before registering' : undefined}
                        >
                          {registeringPinSenderId === sender.id ? 'Registering...' : 'Register By Pin'}
                        </button>
                      )}
                    </div>
                    {registerPinErrors[sender.id] && (
                      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                        {registerPinErrors[sender.id]}
                      </div>
                    )}
                  </td>
                  <td>{sender.status ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(sender.createdTimestamp).toLocaleString()}</td>
                  <td>{new Date(sender.updatedTimestamp).toLocaleString()}</td>
                  <td>{sender.lastUpdatedBy}</td>
                  {canManageWhatsAppSetup && (
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleSyncInfo(sender)}
                          disabled={syncingSenderId === sender.id}
                        >
                          {syncingSenderId === sender.id ? 'Syncing...' : 'Sync'}
                        </button>
                        <button onClick={() => handleEditClick(sender)}>Edit</button>
                        <button onClick={() => toggleActivationStatus(sender.id, sender.status)}>
                          {sender.status ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                      {syncMessages[sender.id] && (
                        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                          {syncMessages[sender.id]}
                        </div>
                      )}
                      {syncErrors[sender.id] && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                          {syncErrors[sender.id]}
                        </div>
                      )}
                      {editErrors[sender.id] && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                          {editErrors[sender.id]}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default WabaSenderDetails;
